import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { api, fmt } from "../lib/api";
import AIExplainer from "../components/AIExplainer";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { Sparkle, Plus, TrendUp, TrendDown, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function StockDetail() {
  const { symbol } = useParams();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [q, o, h] = await Promise.all([
          api.get(`/stocks/quote/${symbol}`),
          api.get(`/stocks/overview/${symbol}`),
          api.get(`/stocks/history/${symbol}`),
        ]);
        setQuote(q.data); setOverview(o.data); setHistory(h.data.points);
      } catch (e) {
        if (e.response?.status === 401) nav("/", { replace: true });
      }
    })();
  }, [symbol]);

  const addToWatch = async () => {
    try { await api.post("/watchlist", { symbol }); toast.success(`${symbol} added to watchlist`); }
    catch { toast.error("Failed"); }
  };

  const up = (quote?.changePercent ?? 0) >= 0;
  const marginData = overview ? [
    { name: "Gross", value: overview.grossMargin ?? 0, fill: "#002FA7" },
    { name: "Operating", value: overview.operatingMargin ?? 0, fill: "#3B82F6" },
    { name: "Net", value: overview.netMargin ?? 0, fill: "#93C5FD" },
  ] : [];

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <button onClick={() => nav(-1)} className="text-xs text-ink2 flex items-center gap-1 mb-6 hover:text-ink" data-testid="detail-back">
          <ArrowLeft size={14} /> Back
        </button>

        {/* Header row */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-eyebrow">{overview?.sector || "—"}</div>
            <h1 className="font-display text-5xl mt-1">{overview?.name || symbol}</h1>
            <div className="font-mono-num text-sm text-ink2 mt-2">{symbol}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="font-mono-num text-5xl">{fmt.money(quote?.price)}</div>
              <div className={`font-mono-num text-sm mt-1 ${up ? "text-pos" : "text-neg"} flex items-center gap-1 justify-end`}>
                {up ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                {fmt.money(quote?.change)} · {fmt.pct(quote?.changePercent)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => setAiOpen(true)} className="btn-primary" data-testid="detail-ask-ai">
                <Sparkle weight="fill" size={14} /> Ask AI
              </button>
              <button onClick={addToWatch} className="btn-ghost text-xs" data-testid="detail-add-watch">
                <Plus size={12} /> Watchlist
              </button>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid lg:grid-cols-12 gap-0 border border-black/10">
          {/* Chart */}
          <div className="lg:col-span-8 p-6 border-r-0 lg:border-r border-black/10 border-b lg:border-b-0">
            <div className="label-eyebrow mb-4">Price · Last 60 days</div>
            <div className="h-[340px]" data-testid="detail-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={40} stroke="#999" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} stroke="#999" width={50} />
                  <Tooltip contentStyle={{ background: "#111", border: "none", color: "#F9F8F6", fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#F9F8F6" }} />
                  <Line type="monotone" dataKey="close" stroke="#002FA7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-8">
              <div className="label-eyebrow mb-2">About the business</div>
              <p className="text-[15px] leading-relaxed text-ink max-w-3xl">
                {overview?.description || "No description available."}
              </p>
            </div>
          </div>

          {/* Right rail */}
          <div className="lg:col-span-4">
            {/* Key stats */}
            <div className="p-6 border-b border-black/10">
              <div className="label-eyebrow mb-3">Key stats</div>
              <dl className="grid grid-cols-2 gap-y-3 gap-x-4 font-mono-num text-sm">
                <dt className="text-ink2">Market cap</dt><dd className="text-right">{fmt.compact(overview?.marketCap)}</dd>
                <dt className="text-ink2">Revenue TTM</dt><dd className="text-right">{fmt.compact(overview?.revenue)}</dd>
                <dt className="text-ink2">P/E ratio</dt><dd className="text-right">{overview?.peRatio ? overview.peRatio.toFixed(1) : "—"}</dd>
                <dt className="text-ink2">Sector</dt><dd className="text-right truncate">{overview?.sector || "—"}</dd>
              </dl>
            </div>

            {/* Profit margins */}
            <div className="p-6 bg-paper2 grain relative">
              <div className="label-eyebrow mb-3">Profit margins</div>
              <div className="font-display text-lg mb-4">How much of $1 of revenue turns into profit?</div>
              <div className="h-[200px]" data-testid="detail-margins">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={marginData} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} unit="%" />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} width={80} />
                    <Tooltip formatter={(v) => `${v.toFixed(1)}%`} contentStyle={{ background: "#111", border: "none", color: "#F9F8F6", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                    <Bar dataKey="value" barSize={22}>
                      {marginData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-xs text-ink2 leading-relaxed mt-3">
                <span className="text-ikb font-semibold">Gross</span> = after cost of goods · <span className="text-blue-500 font-semibold">Operating</span> = after running costs · <span className="text-blue-300 font-semibold">Net</span> = after tax &amp; interest.
              </div>
            </div>
          </div>
        </div>
      </main>

      <AIExplainer open={aiOpen} onOpenChange={setAiOpen} symbol={symbol} companyName={overview?.name || symbol} />
    </div>
  );
}
